import { addDays, addWeeks, addMonths, addYears, isBefore, isAfter, startOfDay } from 'date-fns';
import type { Event } from '@/hooks/useEvents';

export interface RecurrenceRule {
  frequency: 'daily' | 'weekly' | 'monthly' | 'yearly';
  interval: number;
  daysOfWeek?: number[]; // For weekly: 0=Sun, 1=Mon, etc.
  dayOfMonth?: number; // For monthly
  endDate?: string;
  count?: number;
}

export function generateRecurringInstances(
  event: Event,
  viewStartDate: Date,
  viewEndDate: Date
): Event[] {
  if (!event.is_recurring || !event.recurrence_rule) {
    return [event];
  }

  try {
    const rule: RecurrenceRule = JSON.parse(event.recurrence_rule);
    const instances: Event[] = [];
    let currentDate = startOfDay(new Date(event.start_time));
    const eventStart = new Date(event.start_time);
    const eventEnd = new Date(event.end_time);
    const originalDuration = eventEnd.getTime() - eventStart.getTime();
    
    const maxEndDate = rule.endDate 
      ? new Date(rule.endDate)
      : addYears(currentDate, 2); // Limit to 2 years if no end date
    
    let occurrenceCount = 0;
    const maxCount = rule.count || 100; // Limit to prevent infinite loops

    while (
      isBefore(currentDate, maxEndDate) && 
      occurrenceCount < maxCount &&
      isBefore(currentDate, addYears(viewEndDate, 1)) // Safety limit
    ) {
      // Check if current date is within view range
      if (!isBefore(currentDate, viewStartDate) && !isAfter(currentDate, viewEndDate)) {
        const instanceStart = new Date(currentDate);
        instanceStart.setHours(eventStart.getHours(), eventStart.getMinutes(), 0, 0);
        
        const instanceEnd = new Date(instanceStart.getTime() + originalDuration);

        instances.push({
          ...event,
          id: `${event.id}_${currentDate.getTime()}`, // Virtual ID for instance
          start_time: instanceStart.toISOString(),
          end_time: instanceEnd.toISOString(),
        });
      }

      occurrenceCount++;

      // Advance to next occurrence based on frequency
      switch (rule.frequency) {
        case 'daily':
          currentDate = addDays(currentDate, rule.interval);
          break;
        case 'weekly':
          currentDate = addWeeks(currentDate, rule.interval);
          break;
        case 'monthly':
          currentDate = addMonths(currentDate, rule.interval);
          break;
        case 'yearly':
          currentDate = addYears(currentDate, rule.interval);
          break;
      }

      // Stop if we've reached the max count
      if (rule.count && occurrenceCount >= rule.count) {
        break;
      }
    }

    return instances;
  } catch (error) {
    console.error('Error generating recurring instances:', error);
    return [event];
  }
}

export function createRecurrenceRuleString(rule: RecurrenceRule): string {
  return JSON.stringify(rule);
}

export function parseRecurrenceRule(ruleString: string): RecurrenceRule | null {
  try {
    return JSON.parse(ruleString);
  } catch {
    return null;
  }
}
